import React, { useState, useEffect } from 'react';
import ToolFooter from '../ui/ToolFooter';
import { trackEvent } from '../analytics/GoogleAnalytics';
import { Helmet } from 'react-helmet';
import AuthorCredit from '../ui/AuthorCredit';
import FreshnessDate, { getFreshnessDateIso } from '../ui/FreshnessDate';


const MileageComparator = ({ isEmbedded = false }) => {
  const [miles, setMiles] = useState(5000);
  const [fuelCosts, setFuelCosts] = useState(1200);
  const [insurance, setInsurance] = useState(800);
  const [repairs, setRepairs] = useState(400);

  const flatRate = miles <= 10000 ? miles * 0.45 : (10000 * 0.45) + ((miles - 10000) * 0.25);
  const actualCosts = Number(fuelCosts) + Number(insurance) + Number(repairs);
  
  useEffect(() => {
    if (miles > 0) {
      const timer = setTimeout(() => {
        trackEvent('tool_completion', { tool_name: 'mileage_vs_actual' });
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [miles, fuelCosts, insurance, repairs]);
  
  const winner = actualCosts > flatRate ? "Actual Costs" : "HMRC Flat Rate";
  const delta = Math.abs(actualCosts - flatRate);

  const maxVal = Math.max(flatRate, actualCosts, 1);
  const flatWidth = (flatRate / maxVal) * 100;
  const actualWidth = (actualCosts / maxVal) * 100;

  const schema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to claim Actual Motor Expenses over 45p Flat Rate",
    "dateModified": getFreshnessDateIso(),
    "step": [
      {"@type": "HowToStep", "text": "Take a photo of every fuel, insurance, and service receipt using your smartphone camera."},
      {"@type": "HowToStep", "text": "Send it via WhatsApp into Zap Expenses immediately to secure a digital timestamp."},
      {"@type": "HowToStep", "text": "Let Zap Expenses separate the business proportion and compile the total at tax year-end to test against the static flat rate."}
    ]
  };

  return (
    <div className="w-full max-w-4xl mx-auto my-12 bg-slate-900/50 backdrop-blur-xl border border-white/5 rounded-3xl p-8 shadow-2xl">

      <Helmet>
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
        <title>Mileage vs Actual Costs Calculator | UK Tax 2026</title>
      </Helmet>

      <FreshnessDate className="mb-4" />
      <div className="mb-8 border-b border-white/5 pb-8">
        <h1 className="text-4xl font-extrabold text-white mb-4">Mileage Flat Rate (45p) vs Actual Costs</h1>
        
        <AuthorCredit />

        <div className="bg-indigo-500/10 border border-indigo-500/30 p-6 rounded-2xl mb-6 shadow-lg">
          <p className="text-xl font-medium leading-relaxed mb-4">
            <strong>"HMRC's 45p mileage rate has been frozen since 2011, while UK motoring costs have risen by 48% in the same period."</strong>
            <br/><br/>
            <strong>"The 45p AMAP rate remains unchanged since April 2011, significantly lagging behind fuel and insurance inflation."</strong>
          </p>
          <div className="text-[10px] text-indigo-300 italic border-t border-indigo-500/20 pt-2">
            Sources: <a href="https://www.gov.uk/government/statistics/measuring-tax-gaps/1-tax-gaps-summary" target="_blank" rel="noopener noreferrer" className="underline hover:text-indigo-400 text-indigo-300">HMRC, Tax Gaps Summary</a> | <a href="https://www.gov.uk/simpler-income-tax-simplified-expenses/vehicles" target="_blank" rel="noopener noreferrer" className="underline hover:text-indigo-400 text-indigo-300">HMRC, Simplified Expenses</a>
          </div>
        </div>
        
        <div className="prose prose-invert max-w-none text-slate-300 mb-8">
          <p>
            Claiming exact costs for fuel, insurance, and repairs traditionally involved hoarding messy glovebox receipts. Today, simply sending images of your motor bills into <strong>Zap Expenses via WhatsApp</strong> allows you to bypass the punitive fixed rate with zero extra administrative effort.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-8">
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider">Annual Business Miles</label>
            <input type="number" value={miles} onChange={(e) => setMiles(Number(e.target.value))} className="w-full bg-black/40 border border-white/10 rounded-xl py-3 px-4 text-white focus:outline-none font-mono" />
          </div>
          <div className="space-y-2">
            <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider">Annual Fuel Costs (£)</label>
            <input type="number" value={fuelCosts} onChange={(e) => setFuelCosts(Number(e.target.value))} className="w-full bg-black/40 border border-white/10 rounded-xl py-3 px-4 text-white focus:outline-none font-mono" />
          </div>
          <div className="space-y-2">
            <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider">Business Insurance (£)</label>
            <input type="number" value={insurance} onChange={(e) => setInsurance(Number(e.target.value))} className="w-full bg-black/40 border border-white/10 rounded-xl py-3 px-4 text-white focus:outline-none font-mono" />
          </div>
          <div className="space-y-2">
            <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider">Repairs/Servicing (£)</label>
            <input type="number" value={repairs} onChange={(e) => setRepairs(Number(e.target.value))} className="w-full bg-black/40 border border-white/10 rounded-xl py-3 px-4 text-white focus:outline-none font-mono" />
          </div>
        </div>

        <div className="flex flex-col justify-center gap-6">
          <div className="space-y-2">
            <div className="flex justify-between items-end mb-1">
              <span className="text-sm font-bold text-slate-300">HMRC 45p Flat Rate</span>
              <span className="text-lg font-mono font-bold text-slate-200">£{flatRate.toFixed(2)}</span>
            </div>
            <div className="w-full bg-black/50 rounded-full h-6 overflow-hidden border border-white/5">
              <div 
                className="h-full bg-amber-500 transition-all duration-500 relative" 
                style={{ width: `${flatWidth}%` }}
              >
                {winner === "HMRC Flat Rate" && <span className="absolute right-2 top-1 text-[10px] font-bold text-black uppercase">Winner</span>}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between items-end mb-1">
              <span className="text-sm font-bold text-slate-300">Actual Costs (Proportioned)</span>
              <span className="text-lg font-mono font-bold text-slate-200">£{actualCosts.toFixed(2)}</span>
            </div>
            <div className="w-full bg-black/50 rounded-full h-6 overflow-hidden border border-white/5">
              <div 
                className="h-full bg-indigo-500 transition-all duration-500 relative" 
                style={{ width: `${actualWidth}%` }}
              >
                {winner === "Actual Costs" && <span className="absolute right-2 top-1 text-[10px] font-bold text-white uppercase">Winner</span>}
              </div>
            </div>
          </div>

          <div className={`mt-6 p-4 rounded-xl border ${winner === 'Actual Costs' ? 'bg-indigo-500/10 border-indigo-500/30' : 'bg-amber-500/10 border-amber-500/30'}`}>
            <p className="text-sm text-slate-300 leading-relaxed">
              By claiming <strong className="text-white">{winner}</strong>, you could reduce your taxable profit by an additional <strong className="text-emerald-400 text-lg">£{delta.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</strong>.
            </p>
          </div>
        </div>
      </div>



      <div className="mt-6 mb-2 text-center">
        <a href="https://www.gov.uk/expenses-and-benefits-business-travel-mileage/rules-for-tax" target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-indigo-400 hover:text-indigo-300 underline">
          Check HMRC guidelines on Actual vs Flat Rate expenses →
        </a>
      </div>

      <ToolFooter />
    </div>
  );
};

export default MileageComparator;
